import React, { useCallback, useEffect, useMemo, useState } from 'react';
import MacroSeriesChart from './MacroSeriesChart';
import { fetchMacroSeries } from '../utils/bondMacroApi';
import { addMonths, daysUntil } from '../utils/xirr';
import './BondDashboard.css';

const HOLDINGS_KEY = 'bond-reset-holdings-v1';
const DEFAULT_HOLDINGS = [
  {
    id: '1',
    name: 'Example FRN',
    index: 'EURIBOR6M',
    marginPct: 1.25,
    lastReset: '2025-09-01',
    freqMonths: 6,
  },
];

/** Observation limits per series frequency (API `days` = row count, not calendar days). */
const RANGE_OPTIONS = [
  { label: '1Y', daily: 365, monthly: 12 },
  { label: '2Y', daily: 730, monthly: 24 },
  { label: '5Y', daily: 1825, monthly: 60 },
  { label: '10Y', daily: 3650, monthly: 120 },
  { label: 'All', daily: null, monthly: null },
];

const DEFAULT_RANGE = 'All';

function loadHoldings() {
  try {
    const raw = localStorage.getItem(HOLDINGS_KEY);
    if (!raw) return DEFAULT_HOLDINGS;
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) && parsed.length ? parsed : DEFAULT_HOLDINGS;
  } catch {
    return DEFAULT_HOLDINGS;
  }
}

function rangeSpec(label) {
  return RANGE_OPTIONS.find((r) => r.label === label) || RANGE_OPTIONS[RANGE_OPTIONS.length - 1];
}

function seriesSpan(bars) {
  if (!bars?.length) return null;
  return `${bars[0].time} → ${bars[bars.length - 1].time} (${bars.length} obs)`;
}

function nextResetDate(lastReset, freqMonths) {
  let next = lastReset;
  while (daysUntil(next) <= 0) {
    next = addMonths(next, freqMonths);
  }
  return next;
}

function resetBadge(days) {
  if (days <= 0) return { label: 'Due', cls: 'overdue' };
  if (days <= 30) return { label: `${days}d`, cls: 'soon' };
  return { label: `${days}d`, cls: 'ok' };
}

function RateCouponResetPanel() {
  const [range, setRange] = useState(DEFAULT_RANGE);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [euribor, setEuribor] = useState(null);
  const [estr, setEstr] = useState(null);
  const [holdings, setHoldings] = useState(loadHoldings);
  const [draft, setDraft] = useState({
    name: '',
    index: 'EURIBOR6M',
    marginPct: '1.00',
    lastReset: '',
    freqMonths: '6',
  });

  useEffect(() => {
    localStorage.setItem(HOLDINGS_KEY, JSON.stringify(holdings));
  }, [holdings]);

  useEffect(() => {
    let cancelled = false;
    const spec = rangeSpec(range);

    async function load() {
      setLoading(true);
      setError(null);
      try {
        const [euriborData, estrData] = await Promise.all([
          fetchMacroSeries('EURIBOR6M', { days: spec.monthly }),
          fetchMacroSeries('ESTR', { days: spec.daily }),
        ]);
        if (cancelled) return;
        setEuribor(euriborData);
        setEstr(estrData);
      } catch (err) {
        if (!cancelled) {
          setError(err.message || 'Failed to load rate series');
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    load();
    return () => {
      cancelled = true;
    };
  }, [range]);

  const resetRows = useMemo(
    () => holdings.map((row) => {
      const next = nextResetDate(row.lastReset, Number(row.freqMonths) || 6);
      const days = daysUntil(next);
      const indexLatest = row.index === 'ESTR' ? estr?.latest : euribor?.latest;
      const fixing = indexLatest?.value != null ? Number(indexLatest.value) : null;
      const coupon = fixing != null ? fixing + Number(row.marginPct || 0) : null;
      const badge = resetBadge(days);
      return { ...row, next, days, fixing, coupon, badge };
    }),
    [holdings, euribor, estr],
  );

  const addHolding = useCallback(() => {
    if (!draft.name.trim() || !draft.lastReset) return;
    setHoldings((prev) => [
      ...prev,
      {
        id: String(Date.now()),
        name: draft.name.trim(),
        index: draft.index,
        marginPct: Number(draft.marginPct) || 0,
        lastReset: draft.lastReset,
        freqMonths: Number(draft.freqMonths) || 6,
      },
    ]);
    setDraft({ name: '', index: 'EURIBOR6M', marginPct: '1.00', lastReset: '', freqMonths: '6' });
  }, [draft]);

  const removeHolding = useCallback((id) => {
    setHoldings((prev) => prev.filter((row) => row.id !== id));
  }, []);

  return (
    <section className="bond-dash-panel bond-dash-panel--fill">
      <div className="bond-dash-header">
        <h3 className="bond-dash-title">Rate &amp; Coupon-Reset Dashboard</h3>
        <p className="bond-dash-subtitle">
          Euribor 6M and €STR fixings from the investing macro API. Use the range control for
          full history (€STR from 2019).
        </p>
      </div>

      <div className="bond-dash-toolbar">
        <span className="bond-dash-range-label">Chart range</span>
        {RANGE_OPTIONS.map(({ label }) => (
          <button
            key={label}
            type="button"
            className={`bond-dash-range-btn${range === label ? ' active' : ''}`}
            onClick={() => setRange(label)}
            disabled={loading}
          >
            {label}
          </button>
        ))}
      </div>

      <div className="bond-dash-body">
        {loading && <div className="bond-dash-loading">Loading rate fixings…</div>}
        {error && <div className="bond-dash-error">{error}</div>}

        {!loading && !error && (
          <>
            <div className="bond-dash-grid-2">
              <div className="bond-dash-card">
                <h4 className="bond-dash-card-title">6M Euribor</h4>
                <div className="bond-dash-stat">
                  {euribor?.latest?.value != null
                    ? `${Number(euribor.latest.value).toFixed(3)}%`
                    : '—'}
                </div>
                <div className="bond-dash-stat-label">
                  {euribor?.latest?.time ? `as of ${euribor.latest.time}` : '—'}
                  {seriesSpan(euribor?.bars) ? ` · ${seriesSpan(euribor?.bars)}` : ''}
                </div>
                <MacroSeriesChart
                  bars={euribor?.bars || []}
                  color="#1763A1"
                  title="Euribor 6M"
                  height={200}
                />
              </div>

              <div className="bond-dash-card">
                <h4 className="bond-dash-card-title">€STR (daily)</h4>
                <div className="bond-dash-stat">
                  {estr?.latest?.value != null
                    ? `${Number(estr.latest.value).toFixed(3)}%`
                    : '—'}
                </div>
                <div className="bond-dash-stat-label">
                  {estr?.latest?.time ? `as of ${estr.latest.time}` : '—'}
                  {seriesSpan(estr?.bars) ? ` · ${seriesSpan(estr?.bars)}` : ''}
                </div>
                <MacroSeriesChart
                  bars={estr?.bars || []}
                  color="#3A17A1"
                  title="€STR"
                  height={200}
                />
              </div>
            </div>

            <div className="bond-dash-card">
              <h4 className="bond-dash-card-title">Reset countdown (local holdings)</h4>
              <div className="bond-dash-form-row">
                <input
                  className="bond-dash-input"
                  placeholder="Name / ISIN"
                  value={draft.name}
                  onChange={(e) => setDraft((d) => ({ ...d, name: e.target.value }))}
                />
                <select
                  className="bond-dash-select"
                  value={draft.index}
                  onChange={(e) => setDraft((d) => ({ ...d, index: e.target.value }))}
                >
                  <option value="EURIBOR6M">Euribor 6M</option>
                  <option value="ESTR">€STR</option>
                </select>
                <input
                  className="bond-dash-input"
                  type="number"
                  step="0.01"
                  placeholder="Margin %"
                  value={draft.marginPct}
                  onChange={(e) => setDraft((d) => ({ ...d, marginPct: e.target.value }))}
                />
                <input
                  className="bond-dash-input"
                  type="date"
                  value={draft.lastReset}
                  onChange={(e) => setDraft((d) => ({ ...d, lastReset: e.target.value }))}
                />
                <input
                  className="bond-dash-input"
                  type="number"
                  min="1"
                  placeholder="Freq (months)"
                  value={draft.freqMonths}
                  onChange={(e) => setDraft((d) => ({ ...d, freqMonths: e.target.value }))}
                />
                <button type="button" className="bond-dash-btn primary" onClick={addHolding}>
                  Add
                </button>
              </div>

              <div className="bond-dash-table-wrap">
                <table className="bond-dash-table">
                  <thead>
                    <tr>
                      <th>Holding</th>
                      <th>Index</th>
                      <th>Fixing</th>
                      <th>Coupon est.</th>
                      <th>Next reset</th>
                      <th>Countdown</th>
                      <th />
                    </tr>
                  </thead>
                  <tbody>
                    {resetRows.map((row) => (
                      <tr key={row.id}>
                        <td>{row.name}</td>
                        <td>{row.index}</td>
                        <td>{row.fixing != null ? `${row.fixing.toFixed(3)}%` : '—'}</td>
                        <td>{row.coupon != null ? `${row.coupon.toFixed(3)}%` : '—'}</td>
                        <td>{row.next}</td>
                        <td>
                          <span className={`bond-dash-badge ${row.badge.cls}`}>
                            {row.badge.label}
                          </span>
                        </td>
                        <td>
                          <button type="button" className="bond-dash-btn" onClick={() => removeHolding(row.id)}>
                            Remove
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </>
        )}
      </div>
    </section>
  );
}

export default RateCouponResetPanel;
